<script lang="ts">
  import { onMount } from 'svelte';

  let scrolled   = $state(false);
  let mobileOpen = $state(false);
  let isDark     = $state(false);

  const links = [
    { href: '/#services',     label: 'Services' },
    { href: '/#how-it-works', label: 'How it works' },
    { href: '/#pricing',      label: 'Pricing' },
    { href: '/blog',          label: 'Blog' },
    { href: '/about',         label: 'About' },
    { href: '/#contact',      label: 'Contact' },
  ];

  onMount(() => {
    const scrollHandler = () => (scrolled = window.scrollY > 24);
    window.addEventListener('scroll', scrollHandler, { passive: true });
    isDark = document.documentElement.classList.contains('dark');
    return () => window.removeEventListener('scroll', scrollHandler);
  });

  function toggleTheme() {
    isDark = !isDark;
    document.documentElement.classList.toggle('dark', isDark);
    localStorage.setItem('bleunk-theme', isDark ? 'dark' : 'light');
  }
</script>

<header class="fixed top-0 left-0 right-0 z-50 px-4 pt-4">
  <nav
    class="mx-auto max-w-6xl flex items-center justify-between h-14 px-5 rounded-2xl transition-all duration-300
    {scrolled
      ? 'bg-void/90 backdrop-blur-xl border border-dim-6 shadow-xl shadow-black/20'
      : 'bg-transparent'}"
  >
    <!-- Logo -->
    <a href="/" data-sveltekit-reload class="flex items-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 rounded-xl px-1.5 -ml-1.5 transition-all">
      <span class="font-display font-bold text-2xl tracking-tighter text-content group-hover:text-[#C9A87C] transition-colors">
        bleunk<span class="text-[#C9A87C]">.</span>
      </span>
    </a>

    <!-- Desktop links -->
    <div class="hidden md:flex items-center gap-1">
      {#each links as link}
        <a
          href={link.href}
          class="px-3.5 py-2 rounded-xl text-sm text-dim-60 hover:text-content hover:bg-dim-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--color-bg))] transition-all duration-150"
        >
          {link.label}
        </a>
      {/each}
    </div>

    <!-- Right side: theme toggle + CTA -->
    <div class="hidden md:flex items-center gap-2">
      <button
        onclick={toggleTheme}
        class="p-2 rounded-xl text-dim-50 hover:text-content hover:bg-dim-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--color-bg))] transition-all duration-150"
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
>
        {#if isDark}
          <svg class="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
          </svg>
        {:else}
          <svg class="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
          </svg>
        {/if}
      </button>
      <a
        href="/#contact"
        class="px-4 py-2 rounded-xl text-sm font-semibold bg-[#C9A87C] hover:bg-[#D4B896] text-[#08080E] transition-all duration-200 shadow-lg shadow-[#C9A87C]/15 hover:shadow-[#C9A87C]/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 focus-visible:ring-offset-1 focus-visible:ring-offset-[rgb(var(--color-bg))]"
      >
        Get in touch
      </a>
    </div>

    <!-- Mobile: theme toggle + hamburger -->
    <div class="md:hidden flex items-center gap-1">
      <button
        onclick={toggleTheme}
        class="p-2 text-dim-50 hover:text-content rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 transition-colors"
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {#if isDark}
          <svg class="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
          </svg>
        {:else}
          <svg class="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
          </svg>
        {/if}
      </button>
      <button
        class="p-2 text-dim-60 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 rounded-lg transition-colors"
        onclick={() => (mobileOpen = !mobileOpen)}
        aria-label="Toggle menu"
        aria-expanded={mobileOpen}
      >
        {#if mobileOpen}
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        {:else}
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        {/if}
      </button>
    </div>
  </nav>

  <!-- Mobile menu -->
  {#if mobileOpen}
    <div class="md:hidden mx-auto max-w-6xl mt-2 p-4 rounded-2xl bg-void/95 backdrop-blur-xl border border-dim-6">
      {#each links as link}
        <a
          href={link.href}
          class="block px-4 py-3 rounded-xl text-sm text-dim-70 hover:text-content hover:bg-dim-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 transition-all"
          onclick={() => (mobileOpen = false)}
        >
          {link.label}
        </a>
      {/each}
      <div class="pt-3 mt-2 border-t border-dim-6">
        <a
          href="/#contact"
          class="block px-4 py-3 rounded-xl text-sm font-semibold text-center bg-[#C9A87C] hover:bg-[#D4B896] text-[#08080E] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A87C]/50 transition-colors"
          onclick={() => (mobileOpen = false)}
        >
          Get in touch
        </a>
      </div>
    </div>
  {/if}
</header>
